import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';

const PROVIDERS = [
  { id: 'openai', name: 'ChatGPT', models: [
    { id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', price: '$$$', hint: 'Самая мощная' },
    { id: 'gpt-5.6-terra', label: 'GPT-5.6 Terra', price: '$$', hint: 'Сбалансированная' },
    { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', price: '$', hint: 'Быстрая и дешёвая' },
    { id: 'gpt-6-astra', label: 'GPT-6 Astra', price: '$$$$', hint: 'Новейшая и самая мощная (дороже всех)' },
  ]},
  { id: 'anthropic', name: 'Claude', models: [
    { id: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4', price: '$$', hint: 'Основная модель' },
    { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', price: '$', hint: 'Быстрая и дешёвая' },
  ]},
  { id: 'image', name: 'Картинки', models: [
    { id: 'gpt-image-2', label: 'GPT Image 2', price: '$$', hint: 'Генерация и редактирование' },
    { id: 'gpt-image-2.5-flare', label: 'GPT Image 2.5 Flare', price: '$$$', hint: 'Новая, выше качество и скорость' },
  ]},
  { id: 'video', name: 'Видео', models: [
    { id: 'sora-2', label: 'Sora 2', price: '$$', hint: 'Видео по описанию' },
    { id: 'sora-2-pro', label: 'Sora 2 Pro', price: '$$$', hint: 'Максимальное качество' },
  ]},
];

// Sora: цена за секунду ролика зависит от модели и разрешения
const VIDEO_MODELS_CFG = {
  'sora-2': {
    sizes: [
      { id: '1280x720', label: '16:9', desc: '1280×720' },
      { id: '720x1280', label: '9:16', desc: '720×1280' },
    ],
    price: { '1280x720': 0.10, '720x1280': 0.10 },
  },
  'sora-2-pro': {
    sizes: [
      { id: '1280x720', label: '16:9', desc: '1280×720' },
      { id: '720x1280', label: '9:16', desc: '720×1280' },
      { id: '1792x1024', label: '16:9 HD', desc: '1792×1024' },
      { id: '1024x1792', label: '9:16 HD', desc: '1024×1792' },
    ],
    price: { '1280x720': 0.30, '720x1280': 0.30, '1792x1024': 0.50, '1024x1792': 0.50 },
  },
};

const VIDEO_DURATIONS = [4, 8, 12];

function videoCost(model, size, seconds) {
  const cfg = VIDEO_MODELS_CFG[model];
  if (!cfg) return 0;
  return (cfg.price[size] || 0) * (seconds || 0);
}

// Ссылки на файлы сервера отдаются только с токеном
function withToken(url) {
  if (!url || !url.startsWith('/api/')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${localStorage.getItem('token')}`;
}

const KNOWN_PROVIDERS = ['openai', 'anthropic', 'image', 'video'];
function normProvider(p) {
  return KNOWN_PROVIDERS.includes(p) ? p : 'openai';
}

const PRICE_COLORS = { '¢': '#00b894', '$': '#00cec9', '$$': '#f39c12', '$$$': '#e74c3c', '$$$$': '#8e44ad' };

const MODEL_PRICES = {
  'gpt-5.6-sol': { input: 5.00, output: 30.00 },
  'gpt-5.6-terra': { input: 2.50, output: 15.00 },
  'gpt-5.6-luna': { input: 1.00, output: 6.00 },
  'gpt-6-astra': { input: 10.00, output: 50.00 },
  'claude-sonnet-4-20250514': { input: 3.00, output: 15.00 },
  'claude-haiku-4-5-20251001': { input: 0.80, output: 4.00 },
};

// Формат = пропорции, разрешение = реальный размер файла в пикселях.
// Итоговый WIDTHxHEIGHT считает сервер (resolveImageSize в server/routes/chat.js),
// т.к. quality у OpenAI на размер не влияет — только size.
const IMAGE_SIZES = [
  { id: 'original', label: 'Исходный', desc: 'Пропорции исходника' },
  { id: '1:1', label: '1:1', desc: 'Квадрат' },
  { id: '3:2', label: '3:2', desc: 'Горизонтальный' },
  { id: '2:3', label: '2:3', desc: 'Вертикальный' },
  { id: '16:9', label: '16:9', desc: 'Широкий' },
  { id: '9:16', label: '9:16', desc: 'Сторис' },
];

const IMAGE_RESOLUTIONS = [
  { id: '1k', label: '1K', desc: '~1024 px, быстро' },
  { id: '2k', label: '2K', desc: '~2048 px' },
  { id: '4k', label: '4K', desc: 'до 3840 px (квадрат — 2880), дольше и дороже' },
];

function calcCost(model, inp, out) {
  const p = MODEL_PRICES[model] || { input: 2.50, output: 10.00 };
  return (inp || 0) * p.input / 1000000 + (out || 0) * p.output / 1000000;
}

function generateSessionId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// Сервер хранит время как UTC-строку "YYYY-MM-DD HH:MM:SS". Парсим её как UTC.
function parseServerTime(s) {
  if (!s) return 0;
  const t = new Date(s.replace(' ', 'T') + 'Z').getTime();
  return Number.isNaN(t) ? 0 : t;
}

// Возобновляем прошлую сессию только если по ней была активность недавно.
// Больше этого промежутка (перерыв/новый день) — начинаем НОВЫЙ чат,
// чтобы диалоги за разные дни не сливались в одну «бесконечную» сессию.
const RESUME_MAX_IDLE_MS = 6 * 60 * 60 * 1000; // 6 часов

function getStorageKey(base, userId) {
  return userId ? `${base}_${userId}` : base;
}

function loadProviderFromStorage(userId) {
  try {
    const saved = localStorage.getItem(getStorageKey('expomaster_provider', userId));
    if (saved) {
      const { provider, model } = JSON.parse(saved);
      if (provider) return { provider, model: model || 'gpt-5.6-terra' };
    }
  } catch {}
  return { provider: 'openai', model: 'gpt-5.6-terra' };
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  const icons = {
    pdf: '\u{1F4D1}', doc: '\u{1F4C4}', docx: '\u{1F4C4}', xls: '\u{1F4CA}', xlsx: '\u{1F4CA}',
    png: '\u{1F5BC}', jpg: '\u{1F5BC}', jpeg: '\u{1F5BC}', gif: '\u{1F5BC}', webp: '\u{1F5BC}',
    txt: '\u{1F4DD}', md: '\u{1F4DD}', csv: '\u{1F4DD}', json: '\u{1F4DD}',
    zip: '\u{1F4E6}', rar: '\u{1F4E6}',
  };
  return icons[ext] || '\u{1F4CE}';
}

function SendToProjectModal({ imageUrl, onClose }) {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState('select'); // 'select' or 'new'

  useEffect(() => {
    api.getEstimatesList().then(setEstimates).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function sendToExisting(estimateId) {
    setSending(true);
    try {
      await api.attachImageToEstimate(estimateId, imageUrl, `project_${Date.now()}.png`);
      alert('Изображение добавлено в проект!');
      onClose();
    } catch (err) { alert('Ошибка: ' + err.message); }
    setSending(false);
  }

  async function createAndSend() {
    if (!newTitle.trim()) return;
    setSending(true);
    try {
      const est = await api.quickCreateEstimate(newTitle.trim());
      await api.attachImageToEstimate(est.id, imageUrl, `project_${Date.now()}.png`);
      alert('Проект создан и изображение добавлено!');
      onClose();
    } catch (err) { alert('Ошибка: ' + err.message); }
    setSending(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', width: '90%', maxWidth: 480, padding: 24 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px' }}>Отправить в проект</h3>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button onClick={() => setMode('select')} style={{
            flex: 1, padding: '8px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer',
            background: mode === 'select' ? 'var(--accent)' : 'var(--bg3)', color: mode === 'select' ? '#fff' : 'var(--text)', fontWeight: 600,
          }}>Выбрать проект</button>
          <button onClick={() => setMode('new')} style={{
            flex: 1, padding: '8px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer',
            background: mode === 'new' ? 'var(--accent)' : 'var(--bg3)', color: mode === 'new' ? '#fff' : 'var(--text)', fontWeight: 600,
          }}>Новый проект</button>
        </div>

        {mode === 'select' && (
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {loading ? <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 20 }}>Загрузка...</div> :
              estimates.length === 0 ? <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 20 }}>Нет проектов. Создайте новый.</div> :
              estimates.map(e => (
                <div key={e.id} onClick={() => !sending && sendToExisting(e.id)} style={{
                  padding: '10px 12px', borderRadius: 6, border: '1px solid var(--border)', marginBottom: 6,
                  cursor: sending ? 'wait' : 'pointer', background: 'var(--bg2)',
                }}
                  onMouseOver={ev => ev.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseOut={ev => ev.currentTarget.style.borderColor = 'var(--border)'}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{e.project_title || e.client_name || 'Без названия'}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>{e.client_name} {e.area_sqm ? '· ' + e.area_sqm + ' кв.м' : ''}</div>
                </div>
              ))
            }
          </div>
        )}

        {mode === 'new' && (
          <div>
            <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Название проекта..."
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: 12 }}
              onKeyDown={e => e.key === 'Enter' && createAndSend()} />
            <button onClick={createAndSend} disabled={!newTitle.trim() || sending} style={{
              width: '100%', padding: '10px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.9rem',
              opacity: (!newTitle.trim() || sending) ? 0.5 : 1,
            }}>{sending ? 'Создаю...' : 'Создать и добавить'}</button>
          </div>
        )}

        <button onClick={onClose} style={{
          marginTop: 12, width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--border)',
          background: 'var(--bg3)', color: 'var(--text2)', cursor: 'pointer', fontSize: '0.85rem',
        }}>Отмена</button>
      </div>
    </div>
  );
}

export default function ChatPage({ user, uiVis = {} }) {
  const userId = user?.id;
  const savedProv = loadProviderFromStorage(userId);
  const [provider, setProvider] = useState(savedProv.provider);
  const [model, setModel] = useState(savedProv.model);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageSize, setImageSize] = useState('1:1');
  const [imageRes, setImageRes] = useState('1k');
  const [videoSize, setVideoSize] = useState('1280x720');
  const [videoSeconds, setVideoSeconds] = useState(4);
  const [videoProgress, setVideoProgress] = useState(null);
  const [projectModal, setProjectModal] = useState(null);

  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(generateSessionId);
  const [limitInfo, setLimitInfo] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const messagesEnd = useRef(null);
  const fileInputRef = useRef(null);

  const isAdmin = user && user.role === 'admin';
  const perms = (user && user.permissions) || {};
  const hasPerm = (p) => isAdmin || !!perms[p];
  // Видимость конкретных моделей проверяется по правам напрямую, БЕЗ обхода для админа:
  // так администратор тоже может скрыть себе лишние модели через раздел «Доступы»
  // (провайдеры целиком — ChatGPT/Claude/картинки/видео — для админа по-прежнему открыты всегда)
  const hasModelPerm = (p) => !!perms[p];

  const uiShow = (id) => uiVis[id] !== false;
  const providerPermMap = { openai: 'chat_openai', anthropic: 'chat_anthropic', image: 'chat_image', video: 'chat_video' };
  const providerUIMap = { openai: 'ui_provider_openai', anthropic: 'ui_provider_anthropic', image: 'ui_provider_image', video: 'ui_provider_video' };
  const visibleProviders = useMemo(() => PROVIDERS
    .filter(p => hasPerm(providerPermMap[p.id]) && uiVis[providerUIMap[p.id]] !== false)
    .map(p => ({
      ...p,
      models: p.models.filter(m => hasModelPerm('model_' + m.id))
    }))
    .filter(p => p.models.length > 0), [isAdmin, perms, uiVis]);

  // Load last active session from server on mount.
  // Берём сессию с самой СВЕЖЕЙ активностью (по last_at, а не по времени старта)
  // и возобновляем её только если перерыв меньше RESUME_MAX_IDLE_MS.
  // Иначе оставляем только что сгенерированный sessionId — начинается новый чат,
  // и сегодняшний диалог сохранится отдельно, а не допишется во вчерашнюю сессию.
  useEffect(() => {
    if (initialLoaded) return;
    api.mySessions().then(serverSessions => {
      const recent = (serverSessions || [])
        .filter(s => s.session_id)
        .sort((a, b) => parseServerTime(b.last_at) - parseServerTime(a.last_at))[0];
      const idle = recent ? Date.now() - parseServerTime(recent.last_at) : Infinity;
      if (recent && idle <= RESUME_MAX_IDLE_MS) {
        api.loadSession(recent.session_id).then(msgs => {
          if (msgs.length > 0) {
            setMessages(msgs.map(m => {
              const msg = { role: m.role, content: m.content };
              if (m.attachment) msg.attachment = m.attachment;
              if (m.files) msg.files = m.files;
              if (m.role === 'assistant' && m.content && m.content.startsWith('![image](')) msg.image = true;
              if (m.role === 'assistant' && m.content && m.content.startsWith('![video](')) msg.video = true;
              return msg;
            }));
            setSessionId(recent.session_id);
            setProvider(normProvider(recent.provider));
            setModel(recent.model);
          }
          setInitialLoaded(true);
        }).catch(() => setInitialLoaded(true));
      } else {
        setInitialLoaded(true);
      }
    }).catch(() => setInitialLoaded(true));
  }, [initialLoaded]);

  useEffect(() => {
    if (visibleProviders.length === 0) return;
    const curProv = visibleProviders.find(p => p.id === provider);
    if (!curProv) {
      setProvider(visibleProviders[0].id);
      setModel(visibleProviders[0].models[0].id);
    } else if (!curProv.models.find(m => m.id === model)) {
      setModel(curProv.models[0].id);
    }
  }, [provider, model, visibleProviders]);

  // Набор разрешений различается у sora-2 и sora-2-pro — держим выбор корректным
  useEffect(() => {
    if (provider !== 'video') return;
    const cfg = VIDEO_MODELS_CFG[model];
    if (cfg && !cfg.sizes.find(sz => sz.id === videoSize)) setVideoSize(cfg.sizes[0].id);
  }, [provider, model, videoSize]);

  useEffect(() => {
    api.checkLimit().then(setLimitInfo).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem(getStorageKey('expomaster_provider', userId), JSON.stringify({ provider, model }));
  }, [provider, model, userId]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = useCallback(async () => {
    try { setSessions(await api.mySessions()); } catch {}
  }, []);

  function switchProvider(p) {
    setProvider(p.id);
    setModel(p.models[0]?.id || '');
    setAttachment(null);
    setMessages([]);
    setVideoProgress(null);
    setSessionId(generateSessionId());
  }

  function newChat() {
    setMessages([]);
    setSessionId(generateSessionId());
    setShowHistory(false);
    setAttachment(null);
    setVideoProgress(null);
  }

  async function loadOldSession(s) {
    try {
      const msgs = await api.loadSession(s.session_id);
      setProvider(normProvider(s.provider));
      setModel(s.model);
      setMessages(msgs.map(m => {
        const msg = { role: m.role, content: m.content };
        if (m.attachment) msg.attachment = m.attachment;
        if (m.files) msg.files = m.files;
        if (m.role === 'assistant' && m.content && m.content.startsWith('![image](')) msg.image = true;
        if (m.role === 'assistant' && m.content && m.content.startsWith('![video](')) msg.video = true;
        return msg;
      }));
      setSessionId(s.session_id);
      setShowHistory(false);
    } catch {}
  }

  async function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      alert('Файл слишком большой (максимум 20 MB)');
      return;
    }
    setUploading(true);
    try {
      const data = await api.uploadChatFile(file);
      setAttachment(data);
      if (provider === 'image' && data.isImage) setImageSize('original');
    } catch (err) {
      alert('Ошибка загрузки: ' + err.message);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const isLimited = limitInfo && limitInfo.limited;

  // Рендер ролика занимает минуты — опрашиваем сервер, пока не будет результата
  async function pollVideo(jobId, onProgress) {
    const deadline = Date.now() + 20 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 5000));
      const st = await api.videoStatus(jobId);
      if (st.status === 'completed') return st;
      if (st.status === 'failed') throw new Error(st.error || 'Генерация не удалась');
      onProgress(st.progress || 0);
    }
    throw new Error('Превышено время ожидания генерации видео (20 мин)');
  }

  async function send() {
    const text = input.trim();
    if ((!text && !attachment) || loading || isLimited) return;
    setInput('');
    const sid = sessionId;
    const currentAttachment = attachment;
    setAttachment(null);

    if (provider === 'video') {
      const userMsg = { role: 'user', content: text };
      if (currentAttachment) userMsg.attachment = currentAttachment;
      setMessages(prev => [...prev, userMsg]);
      setLoading(true);
      setVideoProgress(0);
      try {
        const refFile = currentAttachment?.isImage ? currentAttachment.filename : null;
        const job = await api.generateVideo(text, model || 'sora-2', videoSize, videoSeconds, sid, refFile);
        const done = await pollVideo(job.id, setVideoProgress);
        setMessages(prev => [...prev, { role: 'assistant', content: `![video](${done.url})`, video: true }]);
      } catch (err) {
        setMessages(prev => [...prev, { role: 'assistant', content: `Ошибка: ${err.message}` }]);
      }
      setVideoProgress(null);
      setLoading(false);
      api.checkLimit().then(setLimitInfo).catch(() => {});
      return;
    }

    if (provider === 'image') {
      const userMsg = { role: 'user', content: text || (currentAttachment ? `[Редактировать: ${currentAttachment.originalName}]` : '') };
      if (currentAttachment) userMsg.attachment = currentAttachment;
      setMessages(prev => [...prev, userMsg]);
      setLoading(true);
      try {
        const editFile = currentAttachment?.isImage ? currentAttachment.filename : null;
        const data = await api.generateImage(text, imageSize, sid, model || 'gpt-image-2', imageRes, editFile);
        setMessages(prev => [...prev, { role: 'assistant', content: `![image](${data.url})`, image: true }]);
      } catch (err) {
        setMessages(prev => [...prev, { role: 'assistant', content: `Ошибка: ${err.message}` }]);
      }
      setLoading(false);
      return;
    }

    const userMsg = { role: 'user', content: text || (currentAttachment ? `[Файл: ${currentAttachment.originalName}]` : '') };
    if (currentAttachment) userMsg.attachment = currentAttachment;

    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      const chatFn = provider === 'openai' ? api.chatOpenAI : api.chatClaude;
      const apiMsgs = newMessages.map(m => {
        const msg = { role: m.role, content: m.content };
        if (m.attachment) msg.attachment = m.attachment;
        if (m.files) msg.files = m.files.map(f => ({ filename: f.filename, originalName: f.originalName }));
        return msg;
      });
      const data = await chatFn(apiMsgs, model, sid);
      const reply = { role: 'assistant', content: data.reply };
      if (data.files?.length) reply.files = data.files;
      setMessages(prev => [...prev, reply]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Ошибка: ${err.message}` }]);
    }
    setLoading(false);
    api.checkLimit().then(setLimitInfo).catch(() => {});
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  const sessionsByDate = {};
  sessions.forEach(s => {
    const date = s.started_at ? s.started_at.slice(0, 10) : '?';
    if (!sessionsByDate[date]) sessionsByDate[date] = [];
    sessionsByDate[date].push(s);
  });

  const isImageProvider = provider === 'image';
  const isVideoProvider = provider === 'video';
  const videoCfg = VIDEO_MODELS_CFG[model] || VIDEO_MODELS_CFG['sora-2'];
  const videoEstimate = videoCost(model, videoSize, videoSeconds);

  return (
    <div className="chat-page">
      <div className="chat-sidebar">
        <h3>Провайдер</h3>
        {visibleProviders.map(p => (
          <button key={p.id} className={`provider-btn ${provider === p.id ? 'active' : ''}`} onClick={() => switchProvider(p)}>
            {p.name}
          </button>
        ))}
        {visibleProviders.find(p => p.id === provider)?.models.length > 0 && (
          <>
            <h3 style={{ marginTop: 16 }}>Модель</h3>
            {(visibleProviders.find(p => p.id === provider)?.models || []).map(m => (
              <button key={m.id} className={`provider-btn ${model === m.id ? 'active' : ''}`} onClick={() => setModel(m.id)}
                style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>{m.label}</span>
                  <span style={{ color: PRICE_COLORS[m.price], fontSize: '0.8rem', fontWeight: 700 }}>{m.price}</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text2)', fontWeight: 400 }}>{m.hint}</div>
              </button>
            ))}
          </>
        )}
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {uiShow('ui_chat_history') && <button className="provider-btn" onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadSessions(); }}>
            {showHistory ? 'Скрыть историю' : 'История чатов'}
          </button>}
          {uiShow('ui_chat_newchat') && <button className="provider-btn" onClick={newChat}>+ Новый чат</button>}
        </div>
      </div>

      {showHistory ? (
        <div className="chat-area" style={{ overflowY: 'auto', padding: 20 }}>
          <h3 style={{ marginBottom: 16 }}>История чатов</h3>
          {sessions.length === 0 && <div style={{ color: 'var(--text2)' }}>Нет истории</div>}
          {Object.keys(sessionsByDate).map(date => (
            <div key={date} style={{ marginBottom: 16 }}>
              <div style={{ color: 'var(--text2)', fontSize: '0.8rem', borderBottom: '1px solid var(--border)', paddingBottom: 4, marginBottom: 8 }}>{date}</div>
              {sessionsByDate[date].map(s => (
                <div key={s.session_id} className="session-card" onClick={() => loadOldSession(s)}>
                  <div className="session-meta">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className={`badge ${s.provider === 'anthropic' ? 'admin' : 'manager'}`}>
                        {s.provider === 'openai' ? 'GPT' : s.provider === 'image' ? 'IMG' : s.provider === 'video' ? 'VIDEO' : 'Claude'}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>{s.model}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem', color: 'var(--text2)', alignItems: 'center' }}>
                      <span>{s.message_count} сообщ.</span>
                      <span>{((s.total_input || 0) + (s.total_output || 0)).toLocaleString()} tok</span>
                      <span style={{ color: 'var(--accent2)' }}>${calcCost(s.model, s.total_input, s.total_output).toFixed(4)}</span>
                      <span>{(calcCost(s.model, s.total_input, s.total_output) * 90).toFixed(2)} ₽</span>
                      <span>{s.started_at?.slice(11, 16)}</span>
                    </div>
                  </div>
                  <div className="session-preview">{s.first_message || '—'}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="chat-area">
          <div className="messages">
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text2)', marginTop: '20vh', opacity: 0.5 }}>
                <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>💬</div>
                <div style={{ fontSize: '0.9rem' }}>Начните диалог</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`message ${m.role}`}>
                <div className="message-label">{m.role === 'user' ? 'Вы' : 'AI'}</div>
                {m.attachment && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                    background: 'rgba(255,255,255,0.1)', borderRadius: 6, marginBottom: 6, fontSize: '0.8rem'
                  }}>
                    <span>{getFileIcon(m.attachment.originalName)}</span>
                    <span style={{ fontWeight: 600 }}>{m.attachment.originalName}</span>
                    <span style={{ opacity: 0.7 }}>({formatFileSize(m.attachment.size)})</span>
                    {m.attachment.isImage && (
                      <img src={m.attachment.url.startsWith('/api/') ? `${m.attachment.url}${m.attachment.url.includes('?') ? '&' : '?'}token=${localStorage.getItem('token')}` : m.attachment.url} alt="" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 4, marginLeft: 'auto' }} />
                    )}
                  </div>
                )}
                {m.video ? (
                  <div>
                    <video
                      src={withToken(m.content.match(/\((.*)\)/)?.[1] || '')}
                      controls
                      style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }}
                    />
                    <a
                      href={withToken(m.content.match(/\((.*)\)/)?.[1] || '')}
                      download
                      style={{
                        display: 'inline-block', margin: '8px 0 0', padding: '6px 16px', borderRadius: 6,
                        border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff',
                        textDecoration: 'none', fontWeight: 700, fontSize: '0.8rem',
                      }}>СКАЧАТЬ MP4</a>
                  </div>
                ) : m.image ? (
                  <div>
                    <img src={(() => { const u = m.content.match(/\((.*)\)/)?.[1] || ''; return u.startsWith('/api/') ? `${u}${u.includes('?') ? '&' : '?'}token=${localStorage.getItem('token')}` : u; })()} alt="generated" />
                    <button onClick={() => setProjectModal(m.content.match(/\((.*)\)/)?.[1])} style={{
                      display: 'block', margin: '8px 0 0', padding: '6px 16px', borderRadius: 6,
                      border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff',
                      cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                    }}>В ПРОЕКТ</button>
                  </div>
                ) : m.content}
                {m.files?.map((f, fi) => (
                  <div key={fi} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginTop: 10,
                    background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.85rem',
                  }}>
                    <span style={{ fontSize: '1.4rem' }}>{getFileIcon(f.originalName)}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.originalName}</div>
                      <div style={{ color: 'var(--text2)', fontSize: '0.75rem' }}>{formatFileSize(f.size || 0)}</div>
                    </div>
                    <a href={withToken(f.url)} download={f.originalName} style={{
                      padding: '6px 16px', borderRadius: 6, border: '1px solid var(--accent)', background: 'var(--accent)',
                      color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap',
                    }}>СКАЧАТЬ</a>
                  </div>
                ))}
              </div>
            ))}
            {loading && (
              <div className="message assistant" style={{ opacity: 0.6 }}>
                {isVideoProvider
                  ? `Генерирую видео... ${videoProgress ? videoProgress + '%' : ''} (обычно 1–5 минут, не закрывайте вкладку)`
                  : 'Думаю...'}
              </div>
            )}
            <div ref={messagesEnd} />
          </div>

          {/* Video settings bar */}
          {isVideoProvider && (
            <div style={{
              display: 'flex', gap: 16, padding: '10px 20px', borderTop: '1px solid var(--border)',
              background: 'var(--bg2)', alignItems: 'center', flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text2)', fontWeight: 600 }}>Формат:</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {videoCfg.sizes.map(sz => (
                    <button key={sz.id} onClick={() => setVideoSize(sz.id)} title={sz.desc}
                      style={{
                        padding: '4px 10px', fontSize: '0.8rem', borderRadius: 6, border: '1px solid var(--border)',
                        background: videoSize === sz.id ? 'var(--accent)' : 'var(--bg3)',
                        color: videoSize === sz.id ? '#fff' : 'var(--text)',
                        cursor: 'pointer', fontWeight: videoSize === sz.id ? 700 : 400,
                        transition: 'all 0.15s',
                      }}>
                      {sz.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text2)', fontWeight: 600 }}>Длительность:</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {VIDEO_DURATIONS.map(d => (
                    <button key={d} onClick={() => setVideoSeconds(d)}
                      style={{
                        padding: '4px 10px', fontSize: '0.8rem', borderRadius: 6, border: '1px solid var(--border)',
                        background: videoSeconds === d ? 'var(--accent)' : 'var(--bg3)',
                        color: videoSeconds === d ? '#fff' : 'var(--text)',
                        cursor: 'pointer', fontWeight: videoSeconds === d ? 700 : 400,
                        transition: 'all 0.15s',
                      }}>
                      {d} сек
                    </button>
                  ))}
                </div>
              </div>
              <span style={{ fontSize: '0.7rem', color: 'var(--text2)', marginLeft: 'auto' }}>
                {videoCfg.sizes.find(sz => sz.id === videoSize)?.desc} · {videoSeconds} сек ·{' '}
                <strong style={{ color: 'var(--accent2)' }}>${videoEstimate.toFixed(2)}</strong>{' '}
                (~{(videoEstimate * 90).toFixed(0)} ₽) за ролик
              </span>
            </div>
          )}

          {/* Image settings bar */}
          {isImageProvider && (
            <div style={{
              display: 'flex', gap: 16, padding: '10px 20px', borderTop: '1px solid var(--border)',
              background: 'var(--bg2)', alignItems: 'center', flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text2)', fontWeight: 600 }}>Формат:</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {IMAGE_SIZES.map(s => (
                    <button key={s.id} onClick={() => setImageSize(s.id)} title={s.desc}
                      style={{
                        padding: '4px 10px', fontSize: '0.8rem', borderRadius: 6, border: '1px solid var(--border)',
                        background: imageSize === s.id ? 'var(--accent)' : 'var(--bg3)',
                        color: imageSize === s.id ? '#fff' : 'var(--text)',
                        cursor: 'pointer', fontWeight: imageSize === s.id ? 700 : 400,
                        transition: 'all 0.15s',
                      }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text2)', fontWeight: 600 }}>Разрешение:</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {IMAGE_RESOLUTIONS.map(q => (
                    <button key={q.id} onClick={() => setImageRes(q.id)} title={q.desc}
                      style={{
                        padding: '4px 10px', fontSize: '0.8rem', borderRadius: 6, border: '1px solid var(--border)',
                        background: imageRes === q.id ? 'var(--accent)' : 'var(--bg3)',
                        color: imageRes === q.id ? '#fff' : 'var(--text)',
                        cursor: 'pointer', fontWeight: imageRes === q.id ? 700 : 400,
                        transition: 'all 0.15s',
                      }}>
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
              <span style={{ fontSize: '0.7rem', color: 'var(--text2)', marginLeft: 'auto' }}>
                {IMAGE_SIZES.find(s => s.id === imageSize)?.desc} · {IMAGE_RESOLUTIONS.find(q => q.id === imageRes)?.desc}
              </span>
            </div>
          )}

          {/* Attachment preview */}
          {attachment && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px',
              borderTop: '1px solid var(--border)', background: 'var(--bg3)', fontSize: '0.85rem'
            }}>
              <span>{getFileIcon(attachment.originalName)}</span>
              {attachment.isImage && (
                <img src={attachment.url.startsWith('/api/') ? `${attachment.url}${attachment.url.includes('?') ? '&' : '?'}token=${localStorage.getItem('token')}` : attachment.url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
              )}
              <span style={{ fontWeight: 600 }}>{attachment.originalName}</span>
              <span style={{ color: 'var(--text2)' }}>({formatFileSize(attachment.size)})</span>
              <button onClick={() => setAttachment(null)} style={{
                marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--danger)',
                cursor: 'pointer', fontSize: '1.1rem', padding: '2px 6px'
              }}>&times;</button>
            </div>
          )}

          {isLimited && (
            <div style={{
              padding: '8px 16px', background: 'rgba(231,76,60,0.12)', color: 'var(--danger)',
              fontSize: '0.8rem', fontWeight: 600, textAlign: 'center',
              borderTop: '1px solid var(--danger)',
            }}>
              Лимит исчерпан (${limitInfo.spent_usd?.toFixed(2)} / ${limitInfo.limit_usd?.toFixed(2)}). Обновление через {limitInfo.days_until_reset} дн.
            </div>
          )}
          <div className="chat-input-wrap">
          <div className="chat-input">
            {uiShow('ui_chat_attach') && <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept={isImageProvider || isVideoProvider ? 'image/*' : undefined}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || isLimited}
                title={isVideoProvider ? 'Прикрепить картинку как первый кадр' : isImageProvider ? 'Прикрепить картинку для редактирования' : 'Прикрепить файл'}
                style={{
                  padding: '12px', background: attachment ? 'var(--accent)' : 'var(--bg3)',
                  color: attachment ? '#fff' : 'var(--text)', border: '1px solid var(--border)',
                  borderRadius: 8, cursor: 'pointer', fontSize: '1.2rem', lineHeight: 1,
                  transition: 'all 0.15s', minWidth: 48,
                }}>
                {uploading ? '...' : '\u{1F4CE}'}
              </button>
            </>}
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isVideoProvider ? 'Опишите видео (сцена, движение камеры, стиль)...' : isImageProvider ? 'Опишите изображение...' : attachment ? `Задайте вопрос по файлу ${attachment.originalName}...` : 'Введите сообщение...'}
              rows={1}
            />
            <button onClick={send} disabled={loading || isLimited || (!input.trim() && !attachment) || (isVideoProvider && !input.trim())}>
              {isLimited ? 'Лимит исчерпан' : '➤'}
            </button>
          </div>
          </div>
        </div>
      )}
      {projectModal && <SendToProjectModal imageUrl={projectModal} onClose={() => setProjectModal(null)} />}
    </div>
  );
}
